import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Hash, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useWorkspace } from '../context/WorkspaceContext';

export default function CreateSpaceChannelModal({ isOpen, onClose, space, onSuccess }) {
    const { currentWorkspace } = useWorkspace();
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (space && isOpen) {
            // Auto-suggest channel name from space name
            const suggestedName = space.name.toLowerCase().replace(/\s+/g, '-');
            setName(suggestedName);
            setDescription(`Channel for ${space.name} space`);
        }
    }, [space, isOpen]);

    const handleSubmit = async () => {
        if (!name.trim()) {
            toast.error('Channel name is required');
            return;
        }

        if (!currentWorkspace || !space) {
            toast.error('Workspace or space not selected');
            return;
        }

        setLoading(true);
        try {
            const { data } = await api.post(`/workspaces/${currentWorkspace.id}/channels`, {
                name: name.trim(),
                description: description.trim(),
                space_id: space.id,
                workspace_id: currentWorkspace.id,
                is_private: true
            });

            toast.success(`Channel #${name} created successfully`);
            setName('');
            setDescription('');
            onClose();
            
            if (onSuccess) {
                onSuccess(data);
            }
        } catch (error) {
            console.error('Failed to create channel:', error);
            toast.error(error.response?.data?.detail || 'Failed to create channel');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (!loading) {
            setName('');
            setDescription('');
            onClose();
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Create Space Channel</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-sm text-blue-800">
                            <strong>Space:</strong> {space?.name}
                        </p>
                        <p className="text-xs text-blue-600 mt-1">
                            This channel will be private and accessible only to space members.
                        </p>
                    </div>
                    
                    <div>
                        <Label>Channel Name <span className="text-red-500">*</span></Label>
                        <div className="flex items-center gap-2 mt-1">
                            <Hash className="w-4 h-4 text-gray-400" />
                            <Input
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="channel-name"
                                className="flex-1"
                                disabled={loading}
                            />
                        </div>
                    </div>
                    
                    <div>
                        <Label>Description</Label>
                        <Textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Channel description"
                            className="mt-1"
                            disabled={loading}
                            rows={3}
                        />
                    </div>
                </div>
                
                <div className="flex justify-end gap-3 mt-6">
                    <Button variant="outline" onClick={handleClose} disabled={loading}>
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleSubmit}
                        className="bg-brand-primary hover:bg-brand-primary-dark"
                        disabled={loading || !name.trim()}
                    >
                        {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        Create Channel
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
